import { setRegisteredData } from "./registered-data";
import {
    startMemory,
    teardownMemory,
    pauseMemory,
    resumeMemory,
    preloadMemoryAssets,
} from "../../../games/memory/memory";

/**
 * In-modal navigation for the multi-step application flow (Memory).
 *
 * Screens live in #modal-form-application as .modal-screen[data-screen="..."] and are
 * shown one at a time. Navigation is driven by data attributes on buttons:
 *   - data-modal-next="<screen>"   → simply show that screen
 *   - data-modal-action="<action>" → run a behaviour (start game, answer, decline)
 *
 * Flow: loading → confirmation → game → result → quest → thanks.
 * There is exactly one game per entry — no trial run and no replay.
 */

const MODAL_SELECTOR = '#modal-form-application';
const MIN_LOADING_MS = 1500; // keep the loading screen up at least this long, even if assets cache instantly
const LOADING_AUTO_PROCEED = true; // TEMP: set true to re-enable auto-advance to confirmation (disabled for styling)
// The game must be started within 1 h of submitting the entry (enforced server-side in
// MemoryController::start). Mirror that window client-side: auto-hide the stale modal after 1 h,
// unless the game has already begun (the timer is cleared on game start).
const MODAL_AUTO_HIDE_MS = 60 * 60 * 1000;
// Countdown turns red under this many milliseconds remaining.
const COUNTDOWN_WARN_MS = 60 * 1000;

let _data = null;
let _uuid = null;
let _score = null;
let _reason = null;
let _autoHideTimer = null;

const $modal = () => $(MODAL_SELECTOR);
const csrf = () => $modal().data('game-csrf');
const endpoint = (action) => `/api/application/${_uuid}/${action}`;

function show(screen) {
    const $m = $modal();
    $m.find('.modal-screen').addClass('d-none');
    $m.find(`.modal-screen[data-screen="${screen}"]`).removeClass('d-none');
    $m.find('.modal-dev-screen').val(screen);
}

/** Entry point: called from the form submit-success handler. */
export function startApplicationModal(data) {
    _data = data;
    _uuid = data.uuid;
    _score = null;
    _reason = null;

    // The submit handler suppresses ajax-form's post-success reset (see application.js), so do
    // it here: the native reset clears the fields AND fires the 'reset' event that mints a fresh
    // idempotency key (see form/idempotency.js), so a follow-up entry isn't rejected as a dupe.
    resetApplicationForm();

    $modal().modal('show');
    armAutoHide();
    runLoading();
}

/** Arm the 1 h auto-hide: once the start window passes, show the "start expired" screen on the
 *  (unstarted) modal — matching what the server would return if the user tried to start now. */
function armAutoHide() {
    clearAutoHide();
    _autoHideTimer = setTimeout(() => {
        _autoHideTimer = null;
        show('start-expired');
    }, MODAL_AUTO_HIDE_MS);
}

/** Cancel the auto-hide timer (e.g. once the game has started, or the modal closed). */
function clearAutoHide() {
    if (_autoHideTimer !== null) {
        clearTimeout(_autoHideTimer);
        _autoHideTimer = null;
    }
}

/** Reset the underlying page form; its native 'reset' event regenerates the idempotency key. */
function resetApplicationForm() {
    $('.application-form').each(function () {
        this.reset();
    });
}

/**
 * Show the loading screen and fill the 10-indicator progress bar in step with the actual
 * asset preload. Once everything is cached, reveal the confirmation screen.
 */
function runLoading() {
    show('loading');

    const startedAt = performance.now();
    const $bar = $modal().find('.loading-stars');
    const $stars = $bar.find('.loading-star').removeClass('is-full');
    const total = $stars.length || 1;
    $bar.attr('aria-valuenow', 0);

    const paint = (progress) => {
        const filled = Math.round(progress * total);
        $stars.each(function (i) {
            $(this).toggleClass('is-full', i < filled);
        });
        $bar.attr('aria-valuenow', filled);
    };

    // Kick off the asset preload, but drive the bar by elapsed time so it fills smoothly
    // and linearly over MIN_LOADING_MS rather than snapping to full on a cache hit.
    let preloadDone = false;
    preloadMemoryAssets().then(() => { preloadDone = true; });

    const tick = () => {
        const timeFrac = Math.min(1, (performance.now() - startedAt) / MIN_LOADING_MS);
        // Advance linearly with time; if assets aren't ready yet, hold just short of full.
        paint(preloadDone ? timeFrac : Math.min(timeFrac, 0.9));

        if (timeFrac >= 1 && preloadDone) {
            paint(1);
            if (!LOADING_AUTO_PROCEED) {
                return; // TEMP: stay on the loading screen for styling
            }
            if (_data) {
                setRegisteredData(_data, MODAL_SELECTOR);
            }
            show('confirmation');
            return;
        }
        requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
}

function gameContainer() {
    return $modal().find('#game-canvas-container').get(0);
}

/**
 * Render the whole-game countdown. The 10-minute budget spans all three levels, so it is
 * owned by the wrapper (the engine's own timer is per level) and only painted here.
 */
function renderCountdown(remainingMs) {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const ss = String(totalSeconds % 60).padStart(2, '0');

    const $countdown = $modal().find('.game-countdown');
    $countdown.find('.game-countdown__value').text(`${mm}:${ss}`);
    $countdown.toggleClass('is-warning', remainingMs <= COUNTDOWN_WARN_MS);
}

/** Live score in the HUD. The authoritative total is still re-scored by /memory/result. */
function renderScore(score) {
    $modal().find('.game-score__value').text(String(Math.max(0, score | 0)).padStart(3, '0'));
}

/**
 * Level indicator: one dot per level, current and completed ones solid, upcoming ones faded.
 * The dots are (re)built when the level count changes, so ROUNDS stays server-side.
 */
function renderLevel(index, total) {
    const $dots = $modal().find('.game-level__dots');

    if ($dots.children().length !== total) {
        $dots.empty();
        for (let i = 0; i < total; i++) {
            $dots.append($('<span class="game-level__dot"></span>'));
        }
    }

    $dots.children().each(function (i) {
        $(this).toggleClass('is-reached', i <= index);
    });
}

/** The score is registered server-side by /memory/result; here we reveal the result screen. */
function startGame() {
    // The game is starting → the 1 h start window no longer applies; stop the auto-hide timer
    // so the modal can't vanish mid-game.
    clearAutoHide();
    renderCountdown(0);
    renderScore(0);
    show('game');

    startMemory(gameContainer(), {
        uuid: _uuid,
        scriptUrl: $modal().data('engine-src') || null,
        onTimeLeft: renderCountdown,
        onScore: renderScore,
        onLevel: renderLevel,
        onDone: function (score, reason) {
            _score = score;
            _reason = reason;
            renderResult();
            show('result');
        },
        onError: function (error) {
            // /start can reject with a code we map to a dedicated screen; anything else falls
            // back to a neutral score-0 result.
            const code = error && error.data && error.data.error;
            if (code === 'outside_hours') {
                return showOutsideHours(error.data);
            }
            if (code === 'start_window_expired') {
                return show('start-expired');
            }
            // Admin hasn't uploaded enough card faces (see MemoryCardCrudController). No
            // GameLog was opened, so the entry keeps its single attempt — stay on the
            // confirmation screen so the player can retry once the pool is filled in.
            if (code === 'not_configured') {
                alert('Gra jest chwilowo niedostępna. Spróbuj ponownie za chwilę.');
                return show('confirmation');
            }
            _score = 0;
            renderResult();
            show('result');
        },
    });
}

/** Fill the "outside play hours" screen with the configured window and reveal it. */
function showOutsideHours(data) {
    const start = data && data.startHour != null ? data.startHour : null;
    const end = data && data.endHour != null ? data.endHour : null;
    if (start != null && end != null) {
        $modal().find('.outside-hours-range').text(`${start}:00–${end}:00`);
    }
    show('outside-hours');
}

function renderResult() {
    const $m = $modal();

    $m.find('.result-score').text(_score ?? 0);
    // Prepend the "czas minął" note only when the 10-minute budget ended the game.
    $m.find('.result-timeout-note').toggleClass('d-none', _reason !== 'timeup');
}

/** Resign from the contest question: run the DECLINE transition and show the thank-you screen. */
function resign(button) {
    const $button = $(button).prop('disabled', true);

    $.ajax({
        type: 'POST',
        url: endpoint('answer'),
        data: { decline: 1, _token: csrf() },
    }).done(function () {
        show('thanks');
    }).fail(function () {
        alert('Nie udało się zapisać rezygnacji. Spróbuj ponownie.');
    }).always(function () {
        $button.prop('disabled', false);
    });
}

function submitAnswer(decline) {
    const $m = $modal();
    const $textarea = $m.find('.quest-answer');
    const $error = $m.find('.quest-answer-error');
    const $buttons = $m.find('[data-modal-action="answer"], [data-modal-action="decline"]');
    const answer = ($textarea.val() || '').trim();

    $error.text('').hide();
    $textarea.removeClass('is-invalid');

    if (!decline && answer === '') {
        $textarea.addClass('is-invalid');
        $error.text('Wpisz odpowiedź').show();
        return;
    }

    $buttons.prop('disabled', true);

    $.ajax({
        type: 'POST',
        url: endpoint('answer'),
        data: { answer: answer, decline: decline ? 1 : 0, _token: csrf() },
    }).done(function () {
        show('thanks');
    }).fail(function (xhr) {
        const message = xhr.responseJSON?.formErrors?.answer?.[0]
            || xhr.responseJSON?.errors?.[0]
            || 'Nie udało się zapisać odpowiedzi. Spróbuj ponownie.';
        $textarea.addClass('is-invalid');
        $error.text(message).show();
    }).always(function () {
        $buttons.prop('disabled', false);
    });
}

// Stop the game and cancel the auto-hide timer whenever the modal is closed.
$(document).on('hidden.bs.modal', MODAL_SELECTOR, function () {
    clearAutoHide();
    teardownMemory();
});

// Orientation guard: on short landscape (mobile) screens the modal shows a "rotate to
// portrait" cover (CSS-only, see s-game-modal.scss — must match this media query). While
// that cover is up over a running game, pause the engine (and the 10-minute budget) and
// resume on rotate back.
const ROTATE_MQ = window.matchMedia('(orientation: landscape) and (max-height: 400px)');

function handleRotateGuard(event) {
    if (!$modal().hasClass('show')) {
        return;
    }
    if (event.matches) {
        pauseMemory();
    } else {
        resumeMemory();
    }
}

if (ROTATE_MQ.addEventListener) {
    ROTATE_MQ.addEventListener('change', handleRotateGuard);
} else if (ROTATE_MQ.addListener) {
    ROTATE_MQ.addListener(handleRotateGuard); // Safari < 14
}

// DEV ONLY: jump to the chosen screen (re-runs loading / renders the result score).
$(document).on('change', `${MODAL_SELECTOR} .modal-dev-screen`, function () {
    const screen = $(this).val();
    teardownMemory();
    if (screen === 'loading') {
        return runLoading();
    }
    if (screen === 'result') {
        renderResult();
    }
    show(screen);
});

// DEV ONLY: open the modal with a stub payload + hardcoded uuid, skipping the form submit.
$(document).on('click', '.application-modal-dev', function () {
    startApplicationModal({
        status: 'OK',
        uuid: $(this).data('dev-uuid'),
        firstname: 'Jan',
        lastname: 'Kowalski',
        email: 'jan.kowalski@example.com',
        phone: '500600700',
        billType: 'Paragon',
        billNr: '0001',
        billDate: '01.01.2026',
        billNip: null,
        billPos: '01',
        prize: null,
        formUrl: null,
    });
});

$(document).on('click', `${MODAL_SELECTOR} [data-modal-next]`, function () {
    show($(this).data('modal-next'));
});

$(document).on('click', `${MODAL_SELECTOR} [data-modal-action]`, function () {
    switch ($(this).data('modal-action')) {
        case 'start-game':
            return startGame();
        case 'resign':
            return resign(this);
        case 'answer':
            return submitAnswer(false);
        case 'decline':
            return submitAnswer(true);
    }
});
